import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Add,
  ChildCare,
  EventNote,
  Group,
  LocationOn,
  Schedule,
  AccountCircle,
  TrendingUp,
  Today,
} from "@material-ui/icons";
import firebase from "../firebase";
import { palette } from "../theme";
import PageContainer from "../Components/UI/PageContainer";

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function StatCard({ icon, label, value, to }) {
  return (
    <Card
      component={Link}
      to={to}
      sx={{
        display: "block",
        textDecoration: "none",
        height: "100%",
        cursor: "pointer",
        transition: "border-color 200ms ease, box-shadow 200ms ease",
        "&:hover": {
          borderColor: palette.primary,
          boxShadow: "0 4px 14px rgba(8, 145, 178, 0.15)",
          textDecoration: "none",
        },
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: palette.primarySoft,
            color: palette.primaryDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: palette.text, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: palette.textSecondary, fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function SessionRow({ session }) {
  return (
    <Box
      component={Link}
      to={"/sessions-profile?sessionID=" + session.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: "12px",
        textDecoration: "none",
        color: palette.text,
        transition: "background-color 150ms ease",
        "&:hover": { backgroundColor: "#F0FAFC", textDecoration: "none", color: palette.text },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          backgroundColor: palette.primarySoft,
          color: palette.primaryDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          flexShrink: 0,
          fontSize: "0.9rem",
        }}
      >
        {(session.fullName || "?")
          .split(" ")
          .map((p) => p[0])
          .slice(0, 2)
          .join("")}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }} noWrap>
          {session.fullName || "Unnamed"}
        </Typography>
        <Typography variant="body2" sx={{ color: palette.textSecondary }} noWrap>
          {session.location || "No location"}
          {session.notes ? " — " + session.notes : ""}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: palette.textSecondary }}>
          {session.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </Typography>
        {session.sessionType && (
          <Chip
            size="small"
            label={session.sessionType === "GroupSession" ? "Group" : "Individual"}
            sx={{
              backgroundColor:
                session.sessionType === "GroupSession" ? "#FEF3C7" : palette.primarySoft,
              color: session.sessionType === "GroupSession" ? "#92400E" : palette.primaryDark,
              height: 22,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function SignedOutHero() {
  return (
    <PageContainer maxWidth={760}>
      <Card sx={{ textAlign: "center", py: 6, px: 3, mt: { xs: 2, md: 8 } }}>
        <CardContent>
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 3,
              borderRadius: "20px",
              background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryLight})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <ChildCare style={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h3" sx={{ color: palette.text, mb: 1 }}>
            Welcome to OT App
          </Typography>
          <Typography variant="body1" sx={{ color: palette.textSecondary, mb: 4, maxWidth: 460, mx: "auto" }}>
            Manage your kids, schools, sessions and session notes — all in one place.
            Sign in to see your day at a glance.
          </Typography>
          <Button
            component={Link}
            to="/sign-in"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AccountCircle />}
          >
            Sign In
          </Button>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default function MainPage() {
  const signedIn = global.config.uid !== "";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ kids: 0, locations: 0, weekCount: 0, todayCount: 0 });
  const [todaySessions, setTodaySessions] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    if (!signedIn) return;
    const db = firebase.firestore();
    const base = db.collection("Main").doc(global.config.uid);

    Promise.all([
      base.collection("Kids").get(),
      base.collection("Locations").get(),
      base.collection("Sessions").orderBy("Date", "desc").limit(100).get(),
    ])
      .then(([kidsSnap, locSnap, sessSnap]) => {
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const sessions = sessSnap.docs.map((doc) => ({
          id: doc.id,
          fullName: doc.data().fullName,
          location: doc.data().Location,
          notes: doc.data().Notes,
          sessionType: doc.data().sessionType,
          date: doc.data().Date ? doc.data().Date.toDate() : new Date(0),
        }));
        const today = sessions.filter((s) => isSameDay(s.date, now));
        const week = sessions.filter((s) => s.date >= weekAgo && s.date <= now);
        setTodaySessions(today);
        setRecentSessions(sessions.slice(0, 6));
        setStats({
          kids: kidsSnap.size,
          locations: locSnap.size,
          weekCount: week.length,
          todayCount: today.length,
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error("Dashboard load failed", err);
        setLoading(false);
      });
  }, [signedIn]);

  if (!signedIn) return <SignedOutHero />;

  return (
    <PageContainer>
      {/* Greeting + quick actions */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: palette.text }}>
            {greeting()}
          </Typography>
          <Typography variant="body1" sx={{ color: palette.textSecondary, mt: 0.5 }}>
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
            {" · here's your day at a glance"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            component={Link}
            to="/sessions-profile"
            variant="contained"
            color="secondary"
            startIcon={<Add />}
          >
            New Session
          </Button>
          <Button component={Link} to="/group-profile" variant="outlined" startIcon={<Group />}>
            New Group
          </Button>
          <Button component={Link} to="/kids-profile" variant="outlined" startIcon={<ChildCare />}>
            New Kid
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          {loading ? (
            <Skeleton variant="rounded" height={96} sx={{ borderRadius: "16px" }} />
          ) : (
            <StatCard icon={<Today />} label="Sessions today" value={stats.todayCount} to="/sessions" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {loading ? (
            <Skeleton variant="rounded" height={96} sx={{ borderRadius: "16px" }} />
          ) : (
            <StatCard icon={<TrendingUp />} label="Sessions this week" value={stats.weekCount} to="/sessions" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {loading ? (
            <Skeleton variant="rounded" height={96} sx={{ borderRadius: "16px" }} />
          ) : (
            <StatCard icon={<ChildCare />} label="Kids" value={stats.kids} to="/kids" />
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {loading ? (
            <Skeleton variant="rounded" height={96} sx={{ borderRadius: "16px" }} />
          ) : (
            <StatCard icon={<LocationOn />} label="Locations" value={stats.locations} to="/locations" />
          )}
        </Grid>
      </Grid>

      {/* Today + Recent */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">Today's Sessions</Typography>
                <Button component={Link} to="/schedule" size="small" startIcon={<Schedule />}>
                  Schedule
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {loading ? (
                <>
                  <Skeleton height={56} />
                  <Skeleton height={56} />
                </>
              ) : todaySessions.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: palette.textSecondary }}>
                  <EventNote style={{ fontSize: 36, opacity: 0.4 }} />
                  <Typography sx={{ mt: 1, fontWeight: 600 }}>No sessions logged today</Typography>
                  <Button
                    component={Link}
                    to="/sessions-profile"
                    size="small"
                    startIcon={<Add />}
                    sx={{ mt: 1 }}
                  >
                    Log a session
                  </Button>
                </Box>
              ) : (
                todaySessions.map((s) => <SessionRow key={s.id} session={s} />)
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">Recent Sessions</Typography>
                <Button component={Link} to="/sessions" size="small">
                  View all
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {loading ? (
                <>
                  <Skeleton height={56} />
                  <Skeleton height={56} />
                </>
              ) : recentSessions.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: palette.textSecondary }}>
                  <Typography sx={{ fontWeight: 600 }}>No sessions yet</Typography>
                </Box>
              ) : (
                recentSessions.map((s) => <SessionRow key={s.id} session={s} />)
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
